/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { LoggerService } from '../../shared/services/logger.service';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { MapConfigService } from '../../map/services/mapConfig.service';
import { RestService } from '../../shared/services/rest.service';
import { FilterOptions, ReportDefinition } from '../state/report.model';
import { ReportDataSource } from '../reportContent/datasource/report.datasource';

export interface RunReportOptions {
    report: ReportDefinition;
    keys?: any[];
    page?: number;
    pageLimit?: number;
    sortColumn?: string;
    sortDirection?: string;
    filterOptions?: FilterOptions[];
}

@Injectable()
export class IsolatedReportService {
    public hasData$: Observable<boolean>;
    public isVolt$: Observable<boolean>;

    public objectKey: any;
    public dataSource: ReportDataSource;

    private _id: string;

    private excelTriggerSubject = new Subject<void>();
    private reportTriggerSubject = new Subject<void>();
    private allDataTriggerSubject = new Subject<void>();
    private enableTableEditSubject = new Subject<void>();
    private markChangedSubject = new Subject<void>();

    private digitizeKeySubject = new BehaviorSubject<any>(null);
    private containerDimensionSubject = new BehaviorSubject<[number, number]>([0, 0]);
    private contentDimensionSubject = new BehaviorSubject<[number, number]>([0, 0]);

    private hasDataSubject = new BehaviorSubject<boolean>(false);
    private isVoltSubject = new BehaviorSubject<boolean>(false);

    constructor(private logger: LoggerService, private mapConfig: MapConfigService, private rest: RestService) {
        this.hasData$ = this.hasDataSubject.asObservable();
        this.isVolt$ = this.isVoltSubject.asObservable();
    }

    public triggerReport() {
        this.reportTriggerSubject.next();
    }

    public triggerAllData() {
        this.allDataTriggerSubject.next();
    }

    public triggerExcelDownload() {
        this.excelTriggerSubject.next();
    }

    public enableTableEdit() {
        this.enableTableEditSubject.next();
    }

    public markChanged() {
        this.markChangedSubject.next();
    }

    public setHasData(value: boolean) {
        this.hasDataSubject.next(value);
    }

    public setIsVolt(value: boolean) {
        this.isVoltSubject.next(value);
    }

    public setDigitizeKey(value) {
        this.digitizeKeySubject.next(value);
    }

    public setContainerDimension(dim: [number, number]) {
        this.containerDimensionSubject.next(dim);
    }

    public setContentDimension(dim: [number, number]) {
        this.contentDimensionSubject.next(dim);
    }

    /**
     * Call the server and get the report data
     * @param opts {RunReportOptions}
     * @returns {*}
     */
    public runReport(opts: RunReportOptions): Observable<any> {
        const reportName = opts.report.name;
        let route;
        let data;

        if ('data' === opts.report.type.toLowerCase()) {
            route = 'report/data/run';
            data = {};
        } else {
            if (!opts.keys) {
                return throwError('No keys for report');
            }
            const layerName = opts.report.layer;
            route = `report/feature/run/${this.mapConfig.mapName}/${layerName}/${opts.report.type}`;
            data = {
                keys: opts.keys,
            };
            if (opts.keys.length === 1) {
                this.setDigitizeKey(opts.keys[0]);
            }
        }
        if ('' !== reportName) {
            data.name = reportName;
        }

        if (opts.page !== null && opts.pageLimit !== null) {
            data.paging = {
                limit: opts.pageLimit,
                page: opts.page,
            };
        }
        if (opts.sortColumn && opts.sortDirection) {
            data.paging = {
                ...data?.paging,
                ...{
                    sortColumn: opts.sortColumn,
                    sortDirection: opts.sortDirection,
                },
            };
        }

        if (null !== opts.filterOptions) {
            data.filters = [];
            for (const filter of opts.filterOptions) {
                const filterData = {
                    type: filter.type,
                    field: filter.field,
                };
                switch (filter.type) {
                    case 'string':
                    case 'string-exact':
                        filterData['value'] = filter.value;
                        break;
                    case 'number':
                        if (filter.min) {
                            filterData['min'] = filter.min;
                        }
                        if (filter.max) {
                            filterData['max'] = filter.max;
                        }
                        break;
                    case 'date':
                        if (filter.min) {
                            filterData['min'] = filter.min;
                        }
                        if (filter.max) {
                            filterData['max'] = filter.max;
                        }
                        break;
                }
                data.filters.push(filterData);
            }
        }
        return this.rest.apiPost(route, data, {}, { useWebWorker: true });
    }

    get id() {
        return this._id;
    }
    set id(value: string) {
        this._id = value;
    }
    get digitizeKey$() {
        return this.digitizeKeySubject.asObservable();
    }

    get containerDimension$() {
        return this.containerDimensionSubject.asObservable();
    }

    get contentDimension$() {
        return this.contentDimensionSubject.asObservable();
    }

    get markChanged$() {
        return this.markChangedSubject.asObservable();
    }

    get enableTableEdit$() {
        return this.enableTableEditSubject.asObservable();
    }

    get allDataTrigger$() {
        return this.allDataTriggerSubject.asObservable();
    }

    get reportTrigger$() {
        return this.reportTriggerSubject.asObservable();
    }

    get excelDownloadTrigger$() {
        return this.excelTriggerSubject.asObservable();
    }
}
